import type * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import { TypeChecker } from '@attain-apps/type-guards'

import type * as libModels from '../../models'

import * as models from './models'
import type { Plugin } from './plugin'
import { State } from './state'

export class Selectors<TTypeParams extends ec.models.TypeParams> {
  // TODO: documentation
  // NOTE: intentionally missing return type so that type can be inferred
  public readonly entityUsageLookup
  // TODO: documentation
  // NOTE: intentionally missing return type so that type can be inferred
  private readonly _getEntityUsage
  /**
   *  TODO: documentation
   *
   * Current list of {@link ec.models.TypeParams.entity entities} that can be
   * consumed by the select component.
   *
   * @see {@link libModels.Item}
   */
  // NOTE: intentionally missing return type so that type can be inferred
  public readonly getAvailableItems
  /** @see {@link State.editingCustomLabel} */
  // NOTE: intentionally missing return type so that type can be inferred
  public readonly editingCustomLabel

  // TODO: documentation
  // NOTE: intentionally missing return type so that type can be inferred
  public readonly getEntityBySelectionTarget
  // TODO: documentation
  // NOTE: intentionally missing return type so that type can be inferred
  public readonly getItemBySelectionTarget
  // TODO: documentation
  // NOTE: intentionally missing return type so that type can be inferred
  public isDeletableBySelectionTarget

  constructor(plugin: Plugin<TTypeParams>) {
    this.editingCustomLabel = plugin.feature.state.project(
      state => state.editingCustomLabel,
    )

    this.entityUsageLookup = plugin.feature.state.project(
      state => state.entityUsageLookup,
    )

    this.getAvailableItems = plugin.feature.createSelectorMethod(
      ({
        // TODO: The selection target is used specifically for creating a new custom
        // label. There is a tightly coupled relationship between 'items' and the
        // handling of 'custom click'. This might need to be refactored in the
        // future, and this code will need to be updated accordingly.
        selectionTarget,
        prepend = [],
        append = [],
        groupId,
      }: {
        readonly selectionTarget: ec.models.Target
        readonly prepend?: readonly libModels.Item[]
        readonly append?: readonly libModels.Item[]
        readonly groupId?: ec.plugins.standard.group.models.Group.Id
      }) =>
        (groupId
          ? plugin.collection.plugins.groupPlugin.feature.selectors
              .getGroupsWithEntities({ groupId })
              .project(groups => groups[0]?.entities ?? [])
          : plugin.collection.plugins.currentListPlugin.feature.selectors
              .entities
        ).project(entities => {
          const items = entities.map<libModels.Item>(entity =>
            plugin.config.getItem(entity),
          )

          items.unshift(...prepend, ...plugin.config.prepend)

          // This needs to be after 'unshift-ing' `prependItems` to ensure that the
          // custom item is always at the top.
          if (plugin.config.createCustom) {
            // place as first item for now, in the future, we might have a dedicate icon
            items.unshift({
              label: 'Custom...',
              onSelect: () => {
                plugin.feature.actions.startEditingCustomLabel
                  .from(`${plugin.id}.Items.Custom.Clicked`)
                  .dispatch({ selectionTarget })
              },
            })
          }

          items.push(...append, ...plugin.config.append)

          return items
        }).asSelector,
    )

    this._getEntityUsage = plugin.feature.createSelectorMethod(
      ({
        id,
        selectionTarget,
      }: ExactlyOneOf<{
        id: ec.models.EntityId
        /**
         * When provided first {@link models.EntityUsage} that matches the
         * {@link models.SelectionTarget target} will be returned
         */
        selectionTarget: ec.models.Target
      }>) =>
        this.entityUsageLookup.project(lookup => {
          if (id) {
            return lookup[id] ? { id, ...lookup[id] } : undefined
          } else if (selectionTarget) {
            return Object.entries(lookup)
              ?.map(([_id, entityUsage]) =>
                entityUsage ? { ...entityUsage, id: _id } : undefined,
              )
              .filter(TypeChecker.isNotNullish.raw)
              .find(({ referencedBy: referencedBySelectionTarget }) =>
                referencedBySelectionTarget?.has(selectionTarget),
              )
          } else {
            // TODO:
            throw new Error('not supported')
          }
        }).asSelector,
    )

    this.getEntityBySelectionTarget = plugin.feature.createSelectorMethod(
      ({ selectionTarget }: { selectionTarget: ec.models.Target }) =>
        this._getEntityUsage({ selectionTarget }).project(
          plugin.collection.plugins.cachePlugin.feature.selectors.lookup
            .asSelector,
          (entityUsage, lookup) =>
            entityUsage ? lookup[entityUsage.id] : undefined,
        ).asSelector,
    )

    this.getItemBySelectionTarget = plugin.feature.createSelectorMethod(
      ({ selectionTarget }: { selectionTarget: ec.models.Target }) =>
        this.getEntityBySelectionTarget({ selectionTarget }).project(
          entity => entity && plugin.config.getItem(entity),
        ).asSelector,
    )

    this.isDeletableBySelectionTarget = plugin.feature.createSelectorMethod(
      ({ selectionTarget }: { selectionTarget: ec.models.Target }) =>
        this._getEntityUsage({ selectionTarget }).project(
          entityUsage => entityUsage?.deletable,
        ).asSelector,
    )
  }
}
